/**
 * A BlockCard: the left-rail, EDITABLE view of one issue content block. It mirrors,
 * in miniature, what the IssueRenderer shows on the right, and lets the owner curate
 * that block in place: rename it, rewrite its prose, reorder it among the kept
 * blocks, and keep or dismiss it. Every edit is handed straight back up through the
 * callbacks; the studio owns the optimistic issue and updates the right pane
 * synchronously, so this card holds only the transient text of its own inputs.
 *
 * A dismissed block collapses to a muted summary with a single Keep control (its
 * text is not editable while set aside), so it can still be found and restored,
 * unlike the reader pane, which drops it entirely.
 */
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@nuff/ui/react';
import type { ContentBlock } from '../../api';
import { ROLE_LABELS, ROLE_TONES } from './IssueRenderer';

/**
 * A request to move keyboard focus to one control on a specific card after a
 * dismiss/keep (`toggle`) or a reorder (`up`/`down`), so a keyboard user is not
 * dropped to the top of the page. Owned by the studio and cleared once taken.
 */
export interface BlockFocusTarget {
  readonly id: string;
  readonly kind: 'toggle' | 'up' | 'down';
}

/** How many characters of prose a dismissed card previews before truncating. */
const PREVIEW_MAX = 140;

/** Roles that carry a reader-visible heading, and so get an editable title field. */
const TITLED_ROLES = new Set(['story', 'digest', 'links']);

/**
 * A short plain-text preview of a block's prose: inline `[text](url)` links are
 * flattened to their text, list markers and extra whitespace are dropped, and the
 * result is truncated. Purely for the dismissed-card glance, never rendered as markup.
 */
export function blockPreview(body: string, max: number = PREVIEW_MAX): string {
  const flat = body
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1') // [text](url) -> text
    .replace(/^\s*[-*]\s+/gm, '') // drop leading list markers
    .replace(/\s+/g, ' ') // collapse whitespace/newlines
    .trim();
  if (flat.length <= max) return flat;
  // Trim back to a word boundary so the ellipsis never splits a word.
  const clipped = flat.slice(0, max);
  const lastSpace = clipped.lastIndexOf(' ');
  const head = lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped;
  return `${head}…`;
}

export function BlockCard({
  block,
  index,
  count,
  busy = false,
  focusTarget = null,
  onFocusHandled,
  highlightId = null,
  onHoverChange,
  onEditingChange,
  onToggleKept,
  onMove,
  onEditTitle,
  onEditBody,
}: {
  readonly block: ContentBlock;
  /** This block's position among the KEPT blocks (-1 when dismissed). */
  readonly index: number;
  /** How many kept blocks there are, so the ends can disable their reorder arrow. */
  readonly count: number;
  /** Disable every control while a load/regenerate owns the issue. */
  readonly busy?: boolean;
  /** A pending focus request from the studio; this card acts on it only if it matches. */
  readonly focusTarget?: BlockFocusTarget | null;
  /** Called once this card has taken the focus, so the studio can clear the request. */
  readonly onFocusHandled?: () => void;
  /** The block currently highlighted across the two panes; this card lights up if it matches. */
  readonly highlightId?: string | null;
  /** Report hover/focus (and leave/blur) so the matching reader block highlights in step. */
  readonly onHoverChange?: (id: string | null) => void;
  /**
   * Report when this card's editor is opened/closed. Opening the editor grows the
   * card and pushes its siblings down, changing the rail layout WITHOUT a reorder,
   * so the studio uses this to keep its FLIP baseline measured from the current
   * layout (otherwise the next reorder animates from stale, pre-expansion rects).
   */
  readonly onEditingChange?: (id: string, editing: boolean) => void;
  readonly onToggleKept: (block: ContentBlock) => void;
  readonly onMove: (block: ContentBlock, direction: -1 | 1) => void;
  readonly onEditTitle: (id: string, title: string) => void;
  readonly onEditBody: (id: string, body: string) => void;
}): React.JSX.Element {
  // The card DEFAULTS to a compact, read-only summary (title + preview) so the rail
  // stays scannable beside the reader render; the title/body inputs are revealed only
  // on an explicit Edit toggle (progressive disclosure). The edit wiring is unchanged:
  // when shown, the inputs still drive the same optimistic-edit handlers.
  const [editing, setEditing] = useState(false);
  // The inputs are controlled by LOCAL draft state (not the issue's block) so the
  // typist keeps trailing spaces and the caret never jumps. The title draft re-seeds
  // from the block only when the block changes UNDER us for a REAL reason (a
  // regenerate mints new ids and remounts; a PATCH rollback reverts the text): since
  // the model stores the title TRIMMED, we reseed only when the stored title differs
  // from the draft's trimmed form, so trimming upstream never yanks a space the
  // typist just added. The body is not trimmed, so it reseeds plainly.
  const [titleDraft, setTitleDraft] = useState(block.title ?? '');
  const [bodyDraft, setBodyDraft] = useState(block.body);
  useEffect(() => {
    setTitleDraft((current) =>
      current.trim() === (block.title ?? '') ? current : (block.title ?? ''),
    );
  }, [block.title]);
  useEffect(() => {
    setBodyDraft(block.body);
  }, [block.body]);

  // Refs to the controls the studio may ask us to focus after a keyboard curation.
  const toggleBtnRef = useRef<HTMLButtonElement | null>(null);
  const upBtnRef = useRef<HTMLButtonElement | null>(null);
  const downBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (focusTarget?.id !== block.id) {
      return;
    }
    let next: HTMLButtonElement | null;
    if (focusTarget.kind === 'toggle') {
      next = toggleBtnRef.current;
    } else {
      // Prefer the arrow that was pressed; if it just became disabled (the block
      // reached an end), fall back to its sibling so focus stays on the card.
      const primary = focusTarget.kind === 'up' ? upBtnRef.current : downBtnRef.current;
      const sibling = focusTarget.kind === 'up' ? downBtnRef.current : upBtnRef.current;
      next = primary !== null && !primary.disabled ? primary : sibling;
    }
    next?.focus();
    onFocusHandled?.();
  }, [focusTarget, block.id, onFocusHandled]);

  const roleLabel = ROLE_LABELS[block.role];
  // A human handle for the reorder/keep aria labels: the block's title, else its
  // role, so the controls read distinctly even when two blocks share a role.
  const handle = block.title !== undefined && block.title.length > 0 ? block.title : roleLabel;
  // A handle for the INPUT labels that ALWAYS disambiguates two same-role blocks:
  // the title, else the role plus this card's 1-based position, so an AT/voice user
  // never has two "Text for the story block" fields collide (#8).
  const fieldHandle =
    block.title !== undefined && block.title.length > 0
      ? block.title
      : `${roleLabel} ${String(index >= 0 ? index + 1 : 1)}`;
  const canTitle = TITLED_ROLES.has(block.role);
  const refCount = block.sourceRefs?.length ?? 0;

  // Two-pane link: hovering or focusing a card highlights (and scrolls to) the
  // matching reader block, and vice versa. onFocus/onBlur bubble from the inner
  // controls, so tabbing into any control lights the pair up. tabIndex=-1 lets a
  // click on the reader block move focus here without adding a tab stop.
  const active = highlightId === block.id;
  const linkProps = {
    tabIndex: -1,
    onMouseEnter: () => onHoverChange?.(block.id),
    onMouseLeave: () => onHoverChange?.(null),
    onFocus: () => onHoverChange?.(block.id),
    onBlur: () => onHoverChange?.(null),
  };

  if (block.status === 'dismissed') {
    const preview = blockPreview(block.body);
    return (
      <article
        className={`nlv__block-card nlv__block-card--dismissed${
          active ? ' nlv__block-card--active' : ''
        }`}
        data-role={block.role}
        data-status="dismissed"
        id={`block-card-${block.id}`}
        {...linkProps}
      >
        <div className="nlv__block-card-head">
          <Badge tone="neutral" variant="tag">
            {roleLabel}
          </Badge>
          {block.title !== undefined && block.title.length > 0 && (
            <h3 className="nlv__block-card-title">{block.title}</h3>
          )}
          {/* An explicit status tag so "set aside" is a clear label, not just a
              dimmed look: it reads at AA and states plainly it will not be sent. */}
          <span className="nlv__block-card-status">Set aside · won't be sent</span>
        </div>
        {preview.length > 0 && <p className="nlv__block-card-preview">{preview}</p>}
        <div className="nlv__block-card-controls">
          <button
            ref={toggleBtnRef}
            type="button"
            className="nlv__section-dismiss"
            aria-label={`Keep ${handle}`}
            disabled={busy}
            onClick={() => onToggleKept(block)}
          >
            Keep
          </button>
        </div>
      </article>
    );
  }

  const preview = blockPreview(block.body);

  return (
    <article
      className={`nlv__block-card${active ? ' nlv__block-card--active' : ''}`}
      data-role={block.role}
      data-status="kept"
      data-editing={editing ? '' : undefined}
      id={`block-card-${block.id}`}
      {...linkProps}
    >
      <div className="nlv__block-card-head">
        <Badge tone={ROLE_TONES[block.role]} variant="tag">
          {roleLabel}
        </Badge>
        {editing && canTitle ? (
          <input
            type="text"
            className="auth__input nlv__block-card-title-input"
            value={titleDraft}
            placeholder="Add a title"
            aria-label={`Title for the ${roleLabel.toLowerCase()} block (${fieldHandle})`}
            disabled={busy}
            onChange={(event) => {
              setTitleDraft(event.target.value);
              onEditTitle(block.id, event.target.value);
            }}
          />
        ) : (
          block.title !== undefined &&
          block.title.length > 0 && <h3 className="nlv__block-card-title">{block.title}</h3>
        )}
      </div>

      {editing ? (
        <label className="nlv__block-card-field">
          <span className="nlv__block-card-field-label">Text</span>
          <textarea
            className="auth__input nlv__textarea nlv__block-card-body-input"
            rows={3}
            value={bodyDraft}
            placeholder="Write this block's text"
            aria-label={`Text for the ${roleLabel.toLowerCase()} block (${fieldHandle})`}
            disabled={busy}
            onChange={(event) => {
              setBodyDraft(event.target.value);
              onEditBody(block.id, event.target.value);
            }}
          />
        </label>
      ) : (
        preview.length > 0 && <p className="nlv__block-card-preview">{preview}</p>
      )}

      {refCount > 0 && (
        <p className="nlv__block-card-refs">
          {String(refCount)} {refCount === 1 ? 'source' : 'sources'}
        </p>
      )}

      <div className="nlv__block-card-controls">
        <div className="nlv__section-order" role="group" aria-label="Reorder block">
          <button
            ref={upBtnRef}
            type="button"
            className="nlv__order-btn"
            aria-label={`Move ${handle} up`}
            disabled={busy || index <= 0}
            onClick={() => onMove(block, -1)}
          >
            ↑
          </button>
          <button
            ref={downBtnRef}
            type="button"
            className="nlv__order-btn"
            aria-label={`Move ${handle} down`}
            disabled={busy || index === -1 || index === count - 1}
            onClick={() => onMove(block, 1)}
          >
            ↓
          </button>
        </div>
        <div className="nlv__block-card-actions">
          {/* Progressive disclosure: Edit reveals the title/body inputs; Done
              collapses back to the compact summary. */}
          <button
            type="button"
            className="nlv__block-edit"
            aria-label={editing ? `Done editing ${handle}` : `Edit ${handle}`}
            aria-expanded={editing}
            disabled={busy}
            onClick={() => {
              const next = !editing;
              setEditing(next);
              onEditingChange?.(block.id, next);
            }}
          >
            {editing ? 'Done' : 'Edit'}
          </button>
          <button
            ref={toggleBtnRef}
            type="button"
            className="nlv__section-dismiss"
            aria-label={`Dismiss ${handle}`}
            disabled={busy}
            onClick={() => onToggleKept(block)}
          >
            Dismiss
          </button>
        </div>
      </div>
    </article>
  );
}
